import React from 'react';
import { ClickableItem, Tooltip, Flex, Text, Flag } from '@upsales/components';
import { useTranslation } from 'Components/Helpers/translate';
import { useSpeechRecognition } from '../useSpeechRecognition';
import BemClass from '@upsales/components/Utils/bemClass';
import { renderToString } from 'react-dom/server';
import { useAIChatDispatch, useAIChatSelector } from '../Context/AIChatContext';
import { setMessage, sendMessage } from '../Context/Helpers';
import { useSelf } from 'App/components/hooks/appHooks';
import { useSoftDeployAccess } from 'App/components/hooks';
import './SpeechRecognitionButton.scss';

export const SpeechRecognitionButton = () => {
	const classes = new BemClass('SpeechRecognitionButton');
	const { t } = useTranslation();
	const dispatch = useAIChatDispatch();
	const { loading } = useAIChatSelector(({ loading, currentChatId }) => ({
		loading: currentChatId ? loading[currentChatId] || false : false
	}));
	const self = useSelf();
	const hasSpeechRecognition = useSoftDeployAccess('AI_CHAT_SPEECH_TO_TEXT');
	const userLanguage = self?.language || self?.userParams?.locale || 'en-US';

	const speechRecognition = useSpeechRecognition(
		hasSpeechRecognition
			? {
					onResult: (transcript, isFinal) => {
						dispatch(setMessage(transcript));
					},
					onError: error => {
						console.warn('Speech recognition error:', error);
					},
					onEnd: finalTranscript => {
						if (finalTranscript.trim() && !loading) {
							dispatch(setMessage(finalTranscript));
							dispatch(sendMessage());
						}
					},
					language: userLanguage,
					continuous: false,
					interimResults: true
			  }
			: {}
	);

	const { isListening, isSupported, startListening, stopListening } = speechRecognition;

	if (!hasSpeechRecognition || !isSupported) {
		return null;
	}

	return (
		<Tooltip
			html={true}
			title={renderToString(
				<Flex gap="u1">
					<Text color="white" size="sm">
						{isListening
							? t('aiChat.stopRecording', 'Stop recording')
							: t('aiChat.startRecording', 'Start recording')}
					</Text>
					<Flag code={userLanguage.split('-')[1].toLowerCase()} />
				</Flex>
			)}
		>
			<ClickableItem
				borderRadius
				icon="microphone"
				onClick={() => {
					if (isListening) {
						stopListening();
					} else {
						startListening();
					}
				}}
				active={isListening}
				disabled={loading}
				size="sm"
				className={classes.elem('button').mod({ active: isListening }).b()}
			/>
		</Tooltip>
	);
};
